import type { PlotWithStats, HeadsectionStats } from "@/types";
import { HeadsectionColumn } from "./HeadsectionColumn";

interface PlotStatsRowProps {
  plot: PlotWithStats;
  onPlotClick?: (plot: PlotWithStats, e: React.MouseEvent) => void;
  /**
   * Fires when the user clicks an individual headsection. If omitted,
   * falls back to `onPlotClick` so a click anywhere in the row opens the
   * plot detail.
   */
  onHeadsectionClick?: (
    plot: PlotWithStats,
    stats: HeadsectionStats,
    e: React.MouseEvent,
  ) => void;
}

const DEFAULT_STATS = Array.from({ length: 7 }, (_, i) => ({
  headsectionNumber: i + 1,
  label: "",
  total: 0,
  completed: 0,
  pending: 0,
  inProgress: 0,
  problem: 0,
  late: 0,
  completedPercent: 0,
  pendingPercent: 0,
  inProgressPercent: 0,
  problemPercent: 0,
  latePercent: 0,
}));

export function PlotStatsRow({ plot, onPlotClick, onHeadsectionClick }: PlotStatsRowProps) {
  const stats = plot.headsectionStats ?? DEFAULT_STATS;
  const handleHeadsectionClick = (hs: HeadsectionStats, e: React.MouseEvent) => {
    if (onHeadsectionClick) {
      onHeadsectionClick(plot, hs, e);
    } else {
      onPlotClick?.(plot, e);
    }
  };

  return (
    // overflow-hidden lets the header's hover tint respect the rounded corners.
    // Padding is now split per-section (instead of a single outer p-3) so the
    // clickable header can extend edge-to-edge of the plot box, matching the
    // project row's click affordance.
    <div className="flex flex-col w-fit max-w-full rounded-lg border bg-white shadow-sm overflow-hidden">
      {/* Clickable header: plot name + action button. Entire row opens the
          plot detail, just like the outer project row opens its detail. */}
      <div
        className="flex items-center justify-between gap-3 px-3 pt-3 pb-2 border-b border-border/50 cursor-pointer"
        onClick={(e) => onPlotClick?.(plot, e)}
      >
        <div
          className="text-sm font-semibold truncate min-w-0"
          title={plot.name}
        >
          {plot.name}
        </div>
      </div>

      {/* Headsection columns — each column has a min width sized so the
          counter row never wraps; truncation kicks in on the section name
          first when there isn't enough room. */}
      <div
        className="grid gap-3 min-w-0 px-3 pt-2.5 pb-3 grid-cols-[repeat(7,clamp(160px,calc(15vw_-_80px),250px))]"
      >
        {stats.map((hs) => (
          <HeadsectionColumn
            key={hs.headsectionNumber}
            stats={hs}
            onClick={handleHeadsectionClick}
          />
        ))}
      </div>
    </div>
  );
}
